import { useMemo } from 'react';
import { Editor } from '@tiptap/react';
import { List } from 'lucide-react';
import {
  TemplateVariableDefinitionDto,
  TemplateVariableType,
} from '@/features/dashboard/messageTemplates/types/TemplateVariableDefinitionDto.ts';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card.tsx';
import {
  getVariableGroup,
  templateVariableDisplayNames,
} from '@/components/richText/templateVariableDisplayNames.ts';
import { isBlockVariable } from '@/components/richText/templateVariableUtils.ts';
import RichTextPreview from '@/components/richText/RichTextPreview.tsx';

const VariableInsertPanel = ({
  editor,
  variables,
}: {
  editor: Editor;
  variables: TemplateVariableDefinitionDto[];
}) => {
  const grouped = useMemo(() => {
    const groups: Record<string, TemplateVariableDefinitionDto[]> = {};
    for (const v of variables) {
      const group = getVariableGroup(v.key) ?? v.key;
      if (!groups[group]) groups[group] = [];
      groups[group].push(v);
    }
    return groups;
  }, [variables]);

  const insertVariable = (variable: TemplateVariableDefinitionDto) => {
    const content = isBlockVariable(variable)
      ? {
          type: 'templateVariableBlock',
          attrs: { variable: variable.key },
        }
      : {
          type: 'templateVariable',
          attrs: {
            variable: variable.key,
            fallback: variable.exampleValue,
          },
        };

    editor.chain().focus().insertContent(content).run();
  };

  return (
    <div className="space-y-1.5 border-b border-neutral-200 bg-neutral-50/50 px-3 py-2">
      <div>
        <div className="text-xs font-semibold text-neutral-700">Zmienne</div>
        <div className="text-[11px] text-neutral-500">
          Kliknij, aby użyć w tekście. Po wysłaniu zmienne zostaną zastępione
          danymi.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {Object.entries(grouped).map(([group, vars]) => (
          <div key={group} className="flex items-start gap-1">
            <span className="pt-0.5 text-xs font-semibold capitalize text-neutral-700">
              {templateVariableDisplayNames[group] ?? group}
            </span>
            <div className="flex flex-wrap items-center gap-1">
              {vars.map((v) => (
                <HoverCard key={v.key} openDelay={200} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <button
                      type="button"
                      onClick={() => insertVariable(v)}
                      className="inline-flex items-center gap-1 rounded-sm border border-dashed border-accent-400 bg-accent-100 px-1.5 py-0.5 text-xs text-neutral-700 transition hover:bg-accent-200 hover:text-neutral-900"
                    >
                      {v.type === TemplateVariableType.LIST && (
                        <List size={12} />
                      )}
                      {templateVariableDisplayNames[v.key] ?? v.key}
                      {v.required && (
                        <span
                          className="text-error-200"
                          title="Zmienna wymagana w treści"
                        >
                          *
                        </span>
                      )}
                    </button>
                  </HoverCardTrigger>
                  <HoverCardContent
                    align="start"
                    className={
                      isBlockVariable(v) ? 'w-auto max-w-2xl p-3' : 'w-auto p-3'
                    }
                  >
                    <div className="mb-2 text-xs font-semibold text-neutral-700">
                      Podgląd
                    </div>
                    {isBlockVariable(v) ? (
                      <RichTextPreview
                        html={v.exampleValue}
                        className="text-sm"
                      />
                    ) : (
                      <div className="text-sm font-medium text-neutral-900">
                        {v.exampleValue}
                      </div>
                    )}
                  </HoverCardContent>
                </HoverCard>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VariableInsertPanel;
